import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Empty, Form, Input, Modal, Row, Space, Spin, Switch, Tag, Typography, message } from "antd";
import { BookOutlined, EditOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";

import { scenarioContextApi } from "../../../../../api/scenarioContextApi";
import { useAuth } from "../../../../../hooks/useAuth";
import { getStandardCostTypeLabel } from "../../../constants";
import MarkdownViewEditor from "./MarkdownViewEditor";

const { Text } = Typography;

const BILLING_RULES_CONTEXT = {
    entryType: "KNOWLEDGE",
    knowledgeDomain: "ap_billing_rules",
    agentName: "ap_invoice_batch_validation_agent",
};

const normalizeText = (value) => String(value ?? "").trim();

const getErrorMessage = (error, fallbackMessage) => error.response?.data?.detail || error.response?.data?.message || error.message || fallbackMessage;

const getSupplierDisplay = (conditions = {}) => {
    if (conditions.supplier_display_name) return conditions.supplier_display_name;
    if (conditions.supplier_code && conditions.supplier_name) return `${conditions.supplier_code} - ${conditions.supplier_name}`;
    return conditions.supplier_name || conditions.supplier_id || "-";
};

const getLabelValue = (labels = {}, keys = []) => {
    for (const key of keys) {
        if (labels[key]) return labels[key];
    }
    return undefined;
};

const BillingRulesMaintenanceModal = ({ visible, onClose, predefinedFilters = {}, predefinedFilterLabels = {} }) => {
    const { hasPermission } = useAuth();
    const [form] = Form.useForm();
    const [record, setRecord] = useState(null);
    const [mode, setMode] = useState("view");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const predefinedSupplierId = predefinedFilters?.scenario_supplier_id;
    const predefinedCostType = predefinedFilters?.scenario_standard_cost_type;
    const hasRequiredContext = Boolean(predefinedSupplierId && predefinedCostType);
    const isEditing = mode === "edit";
    const canCreate = hasPermission("agent:create");
    const canUpdate = hasPermission("agent:update");

    const fixedContext = useMemo(() => {
        const conditions = record?.scenario_conditions || {};
        const recordSupplierDisplay = getSupplierDisplay(conditions);
        const supplierLabel = getLabelValue(predefinedFilterLabels, [
            "scenario_supplier_label",
            "scenario_supplier_name",
            "supplierLabel",
            "supplierName",
            "supplier_name",
        ]) || (recordSupplierDisplay === "-" ? undefined : recordSupplierDisplay) || predefinedSupplierId || "-";
        const costTypeLabel = getLabelValue(predefinedFilterLabels, [
            "scenario_standard_cost_type_label",
            "standardCostTypeLabel",
            "standard_cost_type_label",
            "costTypeLabel",
        ]) || getStandardCostTypeLabel(predefinedCostType || conditions.standard_cost_type);

        return { supplierLabel, costTypeLabel };
    }, [predefinedCostType, predefinedFilterLabels, predefinedSupplierId, record]);

    useEffect(() => {
        if (!visible) {
            setRecord(null);
            setMode("view");
            setLoading(false);
            setSaving(false);
            form.resetFields();
            return undefined;
        }

        if (!hasRequiredContext) {
            setRecord(null);
            setMode("view");
            setLoading(false);
            form.resetFields();
            return undefined;
        }

        let cancelled = false;
        const loadRecord = async () => {
            setLoading(true);
            try {
                const response = await scenarioContextApi.getList({
                    page: 1,
                    page_size: 1,
                    agent_name: BILLING_RULES_CONTEXT.agentName,
                    entry_type: BILLING_RULES_CONTEXT.entryType,
                    scenario_knowledge_domain: BILLING_RULES_CONTEXT.knowledgeDomain,
                    scenario_supplier_id: predefinedSupplierId,
                    scenario_standard_cost_type: predefinedCostType,
                });

                if (cancelled) return;
                setRecord(response.data?.items?.[0] || null);
                setMode("view");
                form.resetFields();
            } catch (error) {
                if (!cancelled) message.error(getErrorMessage(error, "查询计费规则失败"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadRecord();

        return () => {
            cancelled = true;
        };
    }, [form, hasRequiredContext, predefinedCostType, predefinedSupplierId, visible]);

    const openEditor = () => {
        if (!record && !canCreate) {
            message.error("您没有新增计费规则的权限");
            return;
        }
        if (record && !canUpdate) {
            message.error("您没有编辑计费规则的权限");
            return;
        }

        form.setFieldsValue({
            rules_text: record?.json_content?.rules_text || "",
            description: record?.description || "",
            is_active: record?.is_active ?? true,
        });
        setMode("edit");
    };

    const cancelEditor = () => {
        form.resetFields();
        setMode("view");
    };

    const buildSubmitPayload = (values) => ({
        agent_name: BILLING_RULES_CONTEXT.agentName,
        entry_type: BILLING_RULES_CONTEXT.entryType,
        scenario_conditions: {
            supplier_id: String(predefinedSupplierId),
            standard_cost_type: predefinedCostType,
            knowledge_domain: BILLING_RULES_CONTEXT.knowledgeDomain,
        },
        prompt_text: null,
        json_content: {
            ...(record?.json_content || {}),
            rules_text: String(values.rules_text ?? ""),
        },
        description: normalizeText(values.description),
        is_active: values.is_active !== false,
    });

    const handleSubmit = async () => {
        if (!hasRequiredContext) {
            message.error("缺少供应商或费用类型，无法保存计费规则");
            return;
        }

        try {
            const values = await form.validateFields();
            const payload = buildSubmitPayload(values);
            setSaving(true);

            const response = record?.id
                ? await scenarioContextApi.update({ id: record.id, data: payload })
                : await scenarioContextApi.create(payload);

            if (response.data?.success === false) {
                throw new Error(response.data?.message || "保存计费规则失败");
            }

            const savedRecord = response.data?.data?.scenario_context || { ...record, ...payload };
            message.success(response.data?.message || "计费规则已保存");
            setRecord(savedRecord);
            setMode("view");
            form.resetFields();
        } catch (error) {
            if (error.errorFields) return;
            message.error(getErrorMessage(error, "保存计费规则失败"));
        } finally {
            setSaving(false);
        }
    };

    const renderFixedContext = () => (
        <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 16px" }}>
            <Row gutter={[16, 8]}>
                <Col xs={24} md={12}>
                    <Text type="secondary">供应商</Text>
                    <div style={{ marginTop: 4 }}>
                        <Tag color="blue">{fixedContext.supplierLabel}</Tag>
                    </div>
                </Col>
                <Col xs={24} md={12}>
                    <Text type="secondary">费用类型</Text>
                    <div style={{ marginTop: 4 }}>
                        <Tag color="orange">{fixedContext.costTypeLabel}</Tag>
                    </div>
                </Col>
            </Row>
        </Card>
    );

    const renderView = () => {
        if (!record) {
            return (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="该供应商该费用类型暂无计费规则配置">
                    <Button type="primary" icon={<PlusOutlined />} disabled={!hasRequiredContext || !canCreate} onClick={openEditor}>
                        创建计费规则
                    </Button>
                </Empty>
            );
        }

        return (
            <Card
                title="计费规则内容"
                extra={(
                    <Space>
                        <Tag color={record.is_active ? "green" : "default"}>{record.is_active ? "启用" : "停用"}</Tag>
                        <Button type="primary" icon={<EditOutlined />} disabled={!canUpdate} onClick={openEditor}>
                            编辑
                        </Button>
                    </Space>
                )}
            >
                {record.description && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={record.description} />}
                <MarkdownViewEditor value={record.json_content?.rules_text || ""} readOnly minRows={18} emptyText="暂无计费规则内容" />
            </Card>
        );
    };

    const renderEditor = () => (
        <Form form={form} layout="vertical">
            <Card size="small" title={record ? "编辑计费规则" : "创建计费规则"} style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item name="is_active" label="状态" valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="停用" />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="description" label="描述说明">
                    <Input allowClear placeholder="可填写计费规则说明" />
                </Form.Item>
            </Card>
            <Form.Item name="rules_text" label="计费规则内容">
                <MarkdownViewEditor
                    defaultMode="write"
                    placeholder={`# 供应商计费规则\n\n## 费用项\n- Landing Fee: 按 MTOW 计算\n\n## 计算公式\nAmount = Quantity × Unit Price`}
                    emptyText="暂无计费规则内容"
                    minRows={18}
                />
            </Form.Item>
        </Form>
    );

    return (
        <Modal
            title={(
                <Space>
                    <BookOutlined style={{ color: "#722ed1" }} />
                    <span>计费规则维护</span>
                </Space>
            )}
            open={visible}
            onCancel={isEditing ? cancelEditor : onClose}
            width={1080}
            className="fullscreen-modal"
            destroyOnHidden
            footer={isEditing ? [
                <Button key="cancel" onClick={cancelEditor}>
                    取消
                </Button>,
                <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
                    保存
                </Button>,
            ] : [
                <Button key="close" onClick={onClose}>
                    关闭
                </Button>,
            ]}
            styles={{ body: { maxHeight: "calc(100vh - 140px)", overflowY: "auto", padding: "16px 24px" } }}
        >
            {renderFixedContext()}
            {!hasRequiredContext && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="缺少固定上下文"
                    description="计费规则维护需要当前供应商和费用类型，当前入口未提供完整预设条件。"
                />
            )}
            {loading ? (
                <div style={{ textAlign: "center", padding: "56px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12 }}>
                        <Text type="secondary">正在加载计费规则...</Text>
                    </div>
                </div>
            ) : isEditing ? renderEditor() : renderView()}
        </Modal>
    );
};

export default BillingRulesMaintenanceModal;
